import { useState } from 'react';
import { getShortLanguageCode, useDictionary } from 'lib/utils';
import { LanguageInfo, LanguageSwitcherProps } from './HeaderTypes';
import classNames from 'classnames/bind';
import styles from './Header.module.scss';
import Button from 'components/helpers/Button/Button';
import Icon from 'components/helpers/Icon';

const cx = classNames.bind(styles);

const loadShortLanguageCodes = (pageLanguages: LanguageInfo[]) => {
  if (!pageLanguages) {
    return;
  }

  for (const lang of pageLanguages) {
    if (!lang.shortCode) {
      lang.shortCode = getShortLanguageCode(lang.code);
    }
  }
};

const switchLanguage = (selectedLangCode: string, currentPage: string) => {
  const $newUrl = `/${selectedLangCode.toLocaleLowerCase()}${currentPage}${document.location.search}`;
  document.location.href = $newUrl;
};

const LanguageList = (props: LanguageSwitcherProps): JSX.Element | null => {
  const { pageLanguages, currentLanguage, currentPage, isMobile, displayMenu, displayType, onClose } = props;
  const currentLanguageCode = getShortLanguageCode(currentLanguage);

  const [selectedLangCode, setSelectedLangCode] = useState(getShortLanguageCode(currentLanguageCode));

  pageLanguages && loadShortLanguageCodes(pageLanguages);

  const $changeLanguageLabel = useDictionary('Change Language');
  const $headingText = useDictionary('Select your preferred language');
  const $closeLabel = useDictionary('Close');

  const $displayType = displayType || 'desktop';
  const $displayTypeClassName = `display-type-${$displayType}`;
  const $radioButtonName = `language-items-${$displayType}`;
  const $divId = 'language-items';

  return (
    <>
      {isMobile && displayMenu ? <div className={cx('language-switcher-overlay')}></div> : null}

      <div
        className={cx(
          'language-switcher-list',
          'flyout',
          $displayTypeClassName,
          { 'mobile-view': isMobile },
          { 'desktop-view': !isMobile }
        )}
        id={$divId}
        aria-label={$headingText}
        aria-hidden={!displayMenu}
        hidden={!displayMenu}
      >
        <div className={cx('mobile-only', 'close-header')}>
          <div className={cx('horizontal-line')} onClick={onClose} />
          <button className={cx('closeIcon')} onClick={onClose} title={$closeLabel}>
            <Icon variant="close" />
          </button>
        </div>

        <span>{$headingText}</span>
        {pageLanguages?.map((langInfo) => (
          <div className={cx('language-item')} key={langInfo.shortCode}>
            <label>
              <input
                type="radio"
                name={$radioButtonName}
                aria-label={langInfo.nativeName}
                checked={langInfo.shortCode === selectedLangCode}
                onChange={() => setSelectedLangCode(langInfo.shortCode || langInfo.code)}
              />
              {langInfo.nativeName}
            </label>
          </div>
        ))}
        <Button
          type="button"
          aria-label={$changeLanguageLabel}
          title={$changeLanguageLabel}
          disabled={currentLanguageCode === selectedLangCode}
          onClick={() => switchLanguage(selectedLangCode, currentPage)}
        >
          {$changeLanguageLabel}
        </Button>
      </div>
    </>
  );
};

export default LanguageList;
